import { Layout } from "@layouts";
import { Card } from "@shadcn";
import { Bar } from "@organisms";
import { PageBreadcrumbs } from "@molecules";
import { Spinner } from "@atoms";
import React, { useState, useEffect } from "react";
import { AlertTriangle, DollarSign, Percent, Check, Info, ClipboardList, Zap } from "lucide-react";
import { LSAGroup, LSAGroupUpdateInput, LSACategory, PagePaths } from "../../../../types";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { lifestyleCategories } from "../categories/categories-data";
import { LIFESTYLE_YEARLY_AMOUNT_LIMIT } from "@config/lifestyle";
import { useTranslation } from "react-i18next";

const colors = require("@theme/colors") as Record<string, any>;

export type LifestyleManageGroupPageTemplateProps = {
  groupDetails: LSAGroup | null;
  isLoading: boolean;
  hasLoaded: boolean;
  error: string | null;
  formData: LSAGroupUpdateInput;
  setFormData: (data: LSAGroupUpdateInput) => void;
  isDirty: boolean;
  onSave: (dataOverride?: LSAGroupUpdateInput) => Promise<void>;
  onDelete: () => void;
  onCancel: () => void;
  validationErrors: Record<string, string>;
  isSaving: boolean;
  isDeleting: boolean;
};

export const LifestyleManageGroupPageTemplate = ({
  groupDetails,
  isLoading,
  hasLoaded,
  error,
  formData,
  setFormData,
  isDirty,
  onSave,
  onDelete,
  onCancel,
  validationErrors,
  isSaving,
  isDeleting,
}: LifestyleManageGroupPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goToPerks = () => navigate(`/dashboard/${PagePaths.perks}`);
  const goToLifestyleMain = () => navigate(`/dashboard/${PagePaths.lifestyle}/main`);
  const normalizeName = (value: string) =>
    value.toLowerCase().replace(/[^a-z0-9]/g, "");

  function buildAvailableCategories(selected: LSACategory[]): LSACategory[] {
    const nowIso = new Date().toISOString();

    const inCatalog = lifestyleCategories.map((cat) => {
      const match = selected.find(
        (sc) => normalizeName(sc.name) === normalizeName(cat.name),
      );

      if (match) {
        return match;
      }

      return {
        id: cat.id,
        name: cat.name,
        description: cat.description,
        examples: cat.examples,
        icon: cat.icon,
        createdAt: nowIso,
        updatedAt: nowIso,
      };
    });

    const customCategories = selected.filter(
      (category) =>
        !lifestyleCategories.some(
          (cat) => normalizeName(cat.name) === normalizeName(category.name),
        ),
    );

    const allCategories = [...inCatalog, ...customCategories];
    const selectedIds = new Set(selected.map((cat) => cat.id));
    const selectedFirst = allCategories.filter((cat) => selectedIds.has(cat.id));
    const unselected = allCategories.filter((cat) => !selectedIds.has(cat.id));

    return [...selectedFirst, ...unselected];
  }

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<LSACategory[]>(formData.categories || []);
  const [displayCategories, setDisplayCategories] = useState<LSACategory[]>(
    () => buildAvailableCategories(formData.categories || []),
  );
  const [categoryChangesDirty, setCategoryChangesDirty] = useState(false);
  const [infoPopoverCategoryId, setInfoPopoverCategoryId] = useState<string | null>(null);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

  // Sync selectedCategories when formData.categories changes externally
  useEffect(() => {
    if (formData.categories) {
      setSelectedCategories(formData.categories);
      setDisplayCategories(buildAvailableCategories(formData.categories));
      setCategoryChangesDirty(false);
    }
  }, [formData.categories]);

  const handleInputChange = (field: keyof LSAGroupUpdateInput, value: any) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const updateNumericField = (
    field: keyof LSAGroupUpdateInput,
    rawValue: string,
    min: number,
    max: number,
    parser: (value: string) => number,
  ) => {
    if (rawValue === "") {
      setFormData({
        ...formData,
        [field]: undefined,
      });
      return;
    }

    const parsedValue = parser(rawValue);
    if (Number.isNaN(parsedValue)) {
      return;
    }

    const clampedValue = Math.min(max, Math.max(min, parsedValue));

    setFormData({
      ...formData,
      [field]: clampedValue,
    });
  };

  const handleCategoryToggle = (category: LSACategory) => {
    const isSelected = selectedCategories.some(cat => cat.id === category.id);
    let updatedCategories: LSACategory[];
    
    if (isSelected) {
      updatedCategories = selectedCategories.filter(cat => cat.id !== category.id);
    } else {
      updatedCategories = [...selectedCategories, category];
    }
    
    setSelectedCategories(updatedCategories);
    setCategoryChangesDirty(true);
  };

  const handleSaveChanges = async () => {
    const updatedFormData = {
      ...formData,
      categories: selectedCategories,
    };

    setFormData(updatedFormData);
    setCategoryChangesDirty(false);
    await onSave(updatedFormData);
  };

  const getCategoryExamples = (category: LSACategory): string[] => {
    if (Array.isArray(category.examples) && category.examples.length > 0) {
      return category.examples;
    }

    if (Array.isArray(category.description)) {
      return category.description;
    }

    if (typeof category.description === "string" && category.description.trim().length > 0) {
      return [category.description];
    }

    return [];
  };

  const handleInfoMouseEnter = (event: React.MouseEvent, categoryId: string) => {
    event.stopPropagation();
    setInfoPopoverCategoryId(categoryId);
  };

  const handleInfoMouseLeave = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setInfoPopoverCategoryId(null);
  };

  const isCategoryInfoVisible = (categoryId: string) => infoPopoverCategoryId === categoryId;

  const getTranslatedCategory = (category: LSACategory, fallbackId?: string) => {
    const tryKey = (key?: string) =>
      key
        ? (t(`lifestyleManageGroup.categories.categoryDetails.${key}`, {
            returnObjects: true,
            defaultValue: null,
          }) as { name?: string; description?: string; examples?: string[] } | null)
        : null;

    return tryKey(category.id) || tryKey(fallbackId) || {};
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmation === groupDetails?.name) {
      setShowDeleteDialog(false);
      setShowFinalConfirm(true);
    } else {
      toast.error(t("lifestyleManageGroup.toasts.deleteNameMismatch"));
    }
  };

  const handleFinalDelete = async () => {
    if (isDeleting) return;

    try {
      await onDelete();
      setShowFinalConfirm(false);
    } catch (error) {
      console.error("Failed to delete group:", error);
      toast.error(t("lifestyleManageGroup.toasts.deleteFailed"));
    }
  };

  const hasUnsavedChanges = isDirty || categoryChangesDirty;

  const handleCancelClick = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesDialog(true);
      return;
    }

    onCancel();
  };

  const handleDiscardChanges = () => {
    setShowUnsavedChangesDialog(false);
    onCancel();
  };

  const handleKeepEditing = () => {
    setShowUnsavedChangesDialog(false);
  };

  if (isLoading) {
    return (
      <Layout
        variant="dashboard"
        header={t("lifestyleManageGroup.header")}
        subheader={<div className="pt-2"><span className="page-subtitle">{t("lifestyleManageGroup.loading.subheader")}</span></div>}
      >
        <div className="flex items-center justify-center h-64">
          <Spinner variant="large"/>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout
        variant="dashboard"
        header={t("lifestyleManageGroup.header")}
        subheader={<div className="pt-2"><span className="page-subtitle">{t("lifestyleManageGroup.error.subheader")}</span></div>}
      >
        <Card className="p-8">
          <div className="text-center text-secondary-dark">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">{t("lifestyleManageGroup.error.title")}</h3>
            <p className="text-sm mb-4">{error}</p>
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              {t("common.goBack")}
            </button>
          </div>
        </Card>
      </Layout>
    );
  }

  if (!groupDetails && hasLoaded && !isLoading) {
    return (
      <Layout
        variant="dashboard"
        header={t("lifestyleManageGroup.header")}
        subheader={<span>{t("lifestyleManageGroup.notFound.subheader")}</span>}
      >
        <Card className="p-8">
          <div className="text-center text-gray-600">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">{t("lifestyleManageGroup.notFound.title")}</h3>
            <p className="text-sm mb-4">{t("lifestyleManageGroup.notFound.description")}</p>
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              {t("common.goBack")}
            </button>
          </div>
        </Card>
      </Layout>
    );
  }

  const isSaveDisabled =
    isSaving || (!isDirty && !categoryChangesDirty) || Object.keys(validationErrors).length > 0;

  const accentButtonClasses =
    "w-full px-4 py-2.5 rounded-full font-medium text-sm transition-colors border bg-white text-primary-medium border-primary-medium hover:bg-primary-lightest";
  
  return (
    <Layout
      variant="dashboard"
      header={t("lifestyleManageGroup.header")}
      subheader={
        <div className="page-subtitle mt-2">
          {t("lifestyleManageGroup.subheader")}{" "}
        </div>
      }
    >
      <div>
      <div className="pb-24">
        <PageBreadcrumbs
          items={[
            { label: t("lifestyleManageGroup.breadcrumbs.benefits"), onClick: goToPerks },
            { label: t("lifestyleManageGroup.breadcrumbs.lifestyleAccount"), onClick: goToLifestyleMain },
            { label: t("lifestyleManageGroup.breadcrumbs.manageGroup") },
          ]}
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-y-7 gap-x-7 lg:gap-x-5 mt-8">
        {/* Left Column - Form */}
        <div className="lg:col-span-2 space-y-5">
          {/* Basic Information */}
          <div className="rounded-2xl border border-neutral-light bg-white p-6 space-y-6">
            <div className="flex flex-row items-center gap-2">
              <div className="section-title">{t("lifestyleManageGroup.form.title")}</div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("lifestyleManageGroup.form.fields.groupName")}
                </label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md input-focus-primary ${
                    validationErrors.name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder={t("lifestyleManageGroup.form.placeholders.groupName")}
                />
                {validationErrors.name && (
                  <p className="text-secondary-dark text-xs mt-1">{validationErrors.name}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("lifestyleManageGroup.form.fields.yearlySpendingLimit")}
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      value={formData.yearlySpendingLimit ?? ""}
                      onChange={(e) =>
                        updateNumericField(
                          "yearlySpendingLimit",
                          e.target.value,
                          LIFESTYLE_YEARLY_AMOUNT_LIMIT.min,
                          LIFESTYLE_YEARLY_AMOUNT_LIMIT.max,
                          parseFloat,
                        )
                      }
                      className={`w-full pl-10 pr-3 py-2 border rounded-md input-focus-primary ${
                        validationErrors.yearlySpendingLimit ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder={t("lifestyleManageGroup.form.placeholders.yearlySpendingLimit")}
                      min={LIFESTYLE_YEARLY_AMOUNT_LIMIT.min}
                      max={LIFESTYLE_YEARLY_AMOUNT_LIMIT.max}
                      step="100"
                  />
                </div>
                  <div className="h-5 text-xs leading-5 text-secondary-dark">
                    {validationErrors.yearlySpendingLimit || ""}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("lifestyleManageGroup.form.fields.coveragePercentage")}
                  </label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      value={formData.coveragePercentage ?? ""}
                      onChange={(e) =>
                        updateNumericField(
                          "coveragePercentage",
                          e.target.value,
                          1,
                          100,
                          (value) => parseInt(value, 10),
                        )
                      }
                      className={`w-full pl-10 pr-3 py-2 border rounded-md input-focus-primary ${
                        validationErrors.coveragePercentage ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder={t("lifestyleManageGroup.form.placeholders.coveragePercentage")}
                      min="1"
                      max="100"
                      step="1"
                    />
                  </div>
                  <div className="h-5 text-xs leading-5 text-secondary-dark">
                    {validationErrors.coveragePercentage || ""}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ marginTop: "-1px" }}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("lifestyleManageGroup.form.fields.resetDate")}
                  </label>
                  <input
                    type="date"
                    value={formData.resetDate || ""}
                    onChange={(e) => handleInputChange("resetDate", e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium bg-gray-100 ${
                      validationErrors.resetDate ? "border-red-500" : "border-gray-300"
                    }`}
                    disabled
                  />
                  {validationErrors.resetDate && (
                    <p className="text-secondary-dark text-xs mt-1">{validationErrors.resetDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("lifestyleManageGroup.form.fields.runOffPeriod")}
                  </label>
                  <input
                    type="number"
                    value={formData.runOffPeriod ?? ""}
                    onChange={(e) =>
                      updateNumericField("runOffPeriod", e.target.value, 1, 365, (value) => parseInt(value, 10))
                    }
                    className={`w-full px-3 py-2 border rounded-md input-focus-primary ${
                      validationErrors.runOffPeriod ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder={t("lifestyleManageGroup.form.placeholders.runOffPeriod")}
                    min="1"
                    max="365"
                    step="1"
                  />
                  {validationErrors.runOffPeriod && (
                    <p className="text-secondary-dark text-xs mt-1">{validationErrors.runOffPeriod}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="prorated"
                  checked={formData.prorated || false}
                  onChange={(e) => handleInputChange("prorated", e.target.checked)}
                  className="h-4 w-4 focus:ring-brand-orange border-brand-orange rounded"
                  style={{ accentColor: colors.primary.medium }}
                />
                <label htmlFor="prorated" className="ml-2 text-sm text-gray-700">
                  {t("lifestyleManageGroup.form.fields.prorated")}
                </label>
              </div>
              </div>
            </div>
          {/* Eligible Categories */}
          <div className="rounded-2xl border border-neutral-light bg-white p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div>
                  <div className="section-title">
                    {t("lifestyleManageGroup.categories.title")}
                  </div>
                  <div className="text-sm text-neutral-dark mt-0.5">
                    {t("lifestyleManageGroup.categories.description", { count: selectedCategories.length })}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto overflow-x-hidden pr-3">
              {displayCategories.map((category: LSACategory) => {
                const isSelected = selectedCategories.some(cat => cat.id === category.id);
                const normalizeName = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
                const baseCategory = lifestyleCategories.find(
                  (cat) => normalizeName(cat.name) === normalizeName(category.name),
                );
                const translatedCategory = getTranslatedCategory(category, baseCategory?.id);
                const categoryExamples = translatedCategory.examples?.length
                  ? translatedCategory.examples
                  : baseCategory?.examples?.length
                    ? baseCategory.examples
                    : getCategoryExamples(category);
                const categoryDescription =
                  translatedCategory.description ||
                  (typeof baseCategory?.description === "string"
                    ? baseCategory.description
                    : typeof category.description === "string"
                      ? category.description
                      : "");
                const categoryName = translatedCategory.name || category.name;

                return (
                  <div
                    key={category.id}
                    className={`flex items-center justify-between gap-4 rounded-2xl cursor-pointer transition-all p-4 ${
                      isSelected
                        ? "border-2 border-primary-mediumish bg-white hover:border-primary-mediumish hover:bg-primary-lightest"
                        : "border-2 border-neutral-light bg-white hover:border-primary-mediumish hover:bg-primary-lightest"
                    }`}
                    onClick={() => handleCategoryToggle(category)}
                  >
                    <div className="flex items-start gap-4 flex-1 pr-2 min-w-0">
                      <div className="h-12 w-12 rounded-xl border border-neutral-light flex items-center justify-center bg-neutral-lightest text-xl">
                        <span>{category.icon}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="text-base font-semibold text-neutral-darkest">{categoryName}</div>
                            <div className="text-xs text-neutral-mediumDark break-words mt-0.5">
                              {categoryDescription}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-7 w-7 rounded-2xl border-2 flex items-center justify-center ${
                          isSelected
                            ? "border-primary-medium bg-primary-medium text-white"
                            : "border-neutral-light bg-white text-transparent"
                        }`}
                      >
                        <Check className="h-4 w-4" />
                      </div>
                      <div className="relative flex-shrink-0" onMouseLeave={() => handleInfoMouseLeave()}>
                        <button
                          type="button"
                          className="p-2 rounded-full text-neutral-mediumDark hover:text-primary-medium focus:outline-none"
                          onMouseEnter={(event) => handleInfoMouseEnter(event, category.id)}
                          onMouseLeave={handleInfoMouseLeave}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Info className="h-5 w-5" />
                        </button>
                        {isCategoryInfoVisible(category.id) ? (
                          <div
                            className="absolute right-0 top-9 z-20 w-64 p-3 rounded-lg shadow-xl border border-neutral-light bg-white text-left"
                            onMouseEnter={(event) => handleInfoMouseEnter(event, category.id)}
                            onMouseLeave={handleInfoMouseLeave}
                          >
                            <p className="text-xs font-semibold text-neutral-darkest mb-2">{t("lifestyleManageGroup.categories.infoTitle")}</p>
                            {categoryExamples.length ? (
                              <ul className="list-disc list-outside pl-4 space-y-1 max-h-48 overflow-auto text-xs text-neutral-dark">
                                {categoryExamples.map((example) => (
                                  <li key={`${category.id}-${example}`}>{example}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs text-neutral-medium">{t("lifestyleManageGroup.categories.noExamples")}</p>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column - Actions */}
        <div className="space-y-6 lg:col-span-1 lg:self-start lg:sticky lg:top-0">
          <div className="rounded-2xl border border-neutral-light bg-white p-6 space-y-6">
            <div className="flex flex-row items-center gap-2">
              <Zap size={20} />
              <div className="section-title">{t("lifestyleManageGroup.actions.title")}</div>
            </div>
            <div className="space-y-3">
              <button
                onClick={handleSaveChanges}
                disabled={isSaveDisabled}
                className={
                  isSaveDisabled
                    ? "w-full px-4 py-2.5 rounded-full font-medium text-sm transition-colors border bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "w-full px-4 py-2.5 rounded-full font-medium text-sm text-white bg-primary-medium hover:bg-primary-dark transition-colors"
                }
              >
                {isSaving ? t("lifestyleManageGroup.actions.saving") : t("lifestyleManageGroup.actions.save")}
              </button>

              <button onClick={handleCancelClick} className={accentButtonClasses}>
                {t("lifestyleManageGroup.actions.cancel")}
              </button>

              <div className="border-t border-gray-200 pt-3 mt-4">
                <button
                  onClick={handleDeleteClick}
                  className="w-full px-4 py-2.5 rounded-full font-medium text-sm bg-white transition-colors"
                  onMouseEnter={(event) => {
                    event.currentTarget.style.color = "#ef4444";
                    event.currentTarget.style.backgroundColor = "#fef2f2";
                  }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.color = "#dc2626";
                  event.currentTarget.style.backgroundColor = "#ffffff";
                }}
                style={{ color: "#dc2626" }}
              >
                  {t("lifestyleManageGroup.actions.delete")}
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>

      {/* Unsaved Changes Dialog */}
      {showUnsavedChangesDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t("lifestyleManageGroup.dialogs.unsaved.title")}</h3>
            <p className="text-sm text-gray-600 mb-5">
              {t("lifestyleManageGroup.dialogs.unsaved.description")}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleKeepEditing}
                className="px-4 py-2 rounded-full border text-sm font-semibold transition-colors"
                style={{
                  backgroundColor: "#FFFFFF",
                  color: colors.primary.medium,
                  borderColor: colors.primary.medium,
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = "#FFE8D6";
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = "#FFFFFF";
                }}
              >
                {t("lifestyleManageGroup.dialogs.unsaved.keepEditing")}
              </button>
              <button
                onClick={handleDiscardChanges}
                className="px-4 py-2 rounded-full text-sm font-semibold transition-colors"
                style={{
                  backgroundColor: colors.primary.medium,
                  color: "#FFFFFF",
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = colors.primary.dark;
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = colors.primary.medium;
                }}
              >
                {t("lifestyleManageGroup.dialogs.unsaved.discard")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && groupDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-secondary-dark" />
              <h3 className="text-2xl font-semibold text-gray-900">{t("lifestyleManageGroup.dialogs.delete.title")}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {t("lifestyleManageGroup.dialogs.delete.description", { name: groupDetails.name })}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t("lifestyleManageGroup.dialogs.delete.confirmPrompt")} <strong>{groupDetails.name}</strong>
            </p>
            <input
              type="text"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
              placeholder={t("lifestyleManageGroup.form.placeholders.deleteConfirmation")}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="px-4 py-2 rounded-full border text-sm font-semibold transition-colors whitespace-nowrap"
                style={{
                  backgroundColor: "#FFFFFF",
                  color: colors.primary.medium,
                  borderColor: colors.primary.medium,
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = "#FFE8D6";
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = "#FFFFFF";
                }}
              >
                {t("lifestyleManageGroup.actions.cancel")}
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-6 py-2.5 rounded-full font-medium text-sm bg-white transition-colors whitespace-nowrap"
                onMouseEnter={(event) => {
                  event.currentTarget.style.color = "#ef4444";
                  event.currentTarget.style.backgroundColor = "#fef2f2";
                }}
                onMouseLeave={(event) => {
                    event.currentTarget.style.color = "#dc2626";
                    event.currentTarget.style.backgroundColor = "#ffffff";
                  }}
                  style={{ color: "#dc2626" }}
                >
                {t("lifestyleManageGroup.dialogs.delete.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Final Confirmation Dialog */}
      {showFinalConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-secondary-dark" />
              <h3 className="text-lg font-semibold text-gray-900">{t("lifestyleManageGroup.dialogs.final.title")}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {t("lifestyleManageGroup.dialogs.final.description")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowFinalConfirm(false)}
                className="px-4 py-2 rounded-full border text-sm font-semibold transition-colors whitespace-nowrap"
                style={{
                  backgroundColor: "#FFFFFF",
                  color: colors.primary.medium,
                  borderColor: colors.primary.medium,
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = "#FFE8D6";
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = "#FFFFFF";
                }}
              >
                {t("lifestyleManageGroup.actions.cancel")}
              </button>
              <button
                onClick={handleFinalDelete}
                disabled={isDeleting}
                className="px-5 py-2 bg-primary-medium text-white rounded-full hover:bg-primary-dark transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isDeleting ? <Spinner variant="small" /> : t("lifestyleManageGroup.actions.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
      <Bar
        variant="bottom"
        onReturnPressed={handleCancelClick}
        onContinuePressed={null}
        isContinueDisabled={false}
      />
      </div>
      </div>
    </Layout>
  );
};
